import { ApolloError } from '@apollo/client';
import { ClientEvents } from 'common/events/ClientEvents';
import React, { useEffect, useState } from 'react';
import {
  CurrentUserFieldsFragment,
  LoginMutation,
  useLoginMutation,
  useResetPasswordMutationMutation,
} from 'src/gqlReactTypings.generated.d';
import { useTextInputCallback } from 'src/shared/hooks/useInputCallback';
import { useTracker } from '../../shared/hooks/useTracker';
import { useHandleLogin } from '../useHandleLogin';
import { useGateType } from './GateTypeContext';
import { useShowLoginOrSignUpModal } from './useShowLoginOrSignUpModal';

export interface LoginWithEmailProps {
  email: string;
  onEmailChange: (event: React.FormEvent<HTMLInputElement>) => void;
  onSuccess?: (isExistingUser: boolean, user: CurrentUserFieldsFragment) => void;
  goBack: () => void;
  errorMessage?: string;
  loginCTA?: string | null;
}

/**
 * Everything the log-in screen does, with nothing about how it looks. Control draws the reset
 * confirmation as a banner above the form and offers a "log in with different account" button; the
 * gate frames draw the confirmation below the form and rely on the back affordance instead.
 */
export const useLoginWithEmailForm = ({ onSuccess, email, errorMessage }: LoginWithEmailProps) => {
  const [emailErrorMessage, setEmailErrorMessage] = useState<string | undefined>();
  const [passwordErrorMessage, setPasswordErrorMessage] = useState<string | undefined>();
  const [genericErrorMessage, setGenericErrorMessage] = useState<string | undefined>(errorMessage);
  const [password, onPasswordChange] = useTextInputCallback('');
  const [bannerMessage, setBannerMessage] = useState<string | undefined>();
  /** Frozen at request time, so editing the field afterwards cannot relabel the confirmation. */
  const [resetSentTo, setResetSentTo] = useState<string | undefined>();

  const tracker = useTracker();
  const gateType = useGateType();
  const { signUpEntryPoint } = useShowLoginOrSignUpModal();
  const handleLogin = useHandleLogin();

  useEffect(() => {
    tracker.track(ClientEvents.LOGIN_MODAL_EMAIL_LOGIN_PAGE, {
      signUpEntryPoint,
      gateType,
    });
  }, [tracker]);

  const onLoginComplete = (data: LoginMutation) => {
    handleLogin('email', data.login);

    if (onSuccess) {
      onSuccess(data.login.isExistingUser, data.login.user);
    }
  };
  const onLoginError = (error: ApolloError) => {
    tracker.track(ClientEvents.LOGIN_FAILED, {
      method: 'email',
      errorMessage: error.message,
    });

    setPasswordErrorMessage('Invalid login credentials. Please try again.');
  };
  const [login, { loading: loginLoading }] = useLoginMutation({ onCompleted: onLoginComplete, onError: onLoginError });

  const [resetPasswordMutation, { loading: resetPasswordLoading }] = useResetPasswordMutationMutation({
    variables: { email },
  });
  const resetMessages = () => {
    setBannerMessage(undefined);
    setResetSentTo(undefined);
    setEmailErrorMessage(undefined);
    setPasswordErrorMessage(undefined);
    setGenericErrorMessage(undefined);
  };

  const requestLoading = resetPasswordLoading || loginLoading;

  const handleSubmitForm = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    if (requestLoading) {
      return;
    }

    resetMessages();

    const emailInputted = email.trim().length > 0;
    if (!emailInputted) {
      setEmailErrorMessage('Enter an email');
    }
    const passwordInputted = password.trim().length > 0;
    if (!passwordInputted) {
      setPasswordErrorMessage('Enter a password');
    }

    if (emailInputted && passwordInputted) {
      tracker.track(ClientEvents.LOGIN_MODAL_EMAIL_LOGIN_SUBMIT, { method: 'email' });
      login({ variables: { email, password } });
    }
  };

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    resetMessages();

    tracker.track(ClientEvents.LOGIN_MODAL_FORGOT_PASSWORD_CLICKED, {
      signUpEntryPoint,
      gateType,
    });

    const emailInputted = email.trim().length > 0;
    if (!emailInputted) {
      setEmailErrorMessage('Enter an email');
    }

    // Optimistically set banner message. We don't throw or do anything if the email isn't registered.
    setBannerMessage(`A link to reset your password has been sent to ${email.trim()}`);
    setResetSentTo(email.trim());
    if (emailInputted) {
      resetPasswordMutation({ variables: { email } });
    }
  };

  // Theoretically this shouldn't be needed – not sure why enter isn't handling form submission like normal
  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitForm();
    }
  };

  return {
    password,
    onPasswordChange,
    emailErrorMessage,
    passwordErrorMessage,
    genericErrorMessage,
    bannerMessage,
    resetSentTo,
    requestLoading,
    handleSubmitForm,
    handleForgotPassword,
    handleKeyDown,
  };
};
